if (!customElements.get('product-zoom-modal')) {
  class ProductZoomModal extends ModalDialog {
    constructor() {
      super();
      this.track = this.querySelector('[data-zoom-track]');
      this.viewport = this.querySelector('[data-zoom-viewport]');
      this.lens = this.querySelector('[data-zoom-lens]');
      this.prevButton = this.querySelector('[data-zoom-prev]');
      this.nextButton = this.querySelector('[data-zoom-next]');
      this.counter = this.querySelector('[data-zoom-counter]');
      this.currentIndex = 0;
      this.slides = [];
      this.cacheKey = '';
      this.mediaQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
      this.zoomScale = 2;
      this.lensSize = 160;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleGalleryClick = this.handleGalleryClick.bind(this);
      this.handleLensMove = this.handleLensMove.bind(this);
      this.handleLensLeave = this.handleLensLeave.bind(this);

      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    }

    isDesktop() {
      return !this.mediaQuery || this.mediaQuery.matches;
    }

    connectedCallback() {
      super.connectedCallback();
      if (this.bound) return;
      this.bound = true;
      const galleryId = this.dataset.galleryId;
      this.gallery = galleryId ? document.getElementById(galleryId) : null;
      if (this.gallery) {
        this.gallery.addEventListener('click', this.handleGalleryClick);
      }
      if (this.viewport) {
        this.viewport.addEventListener('pointermove', this.handleLensMove);
        this.viewport.addEventListener('pointerleave', this.handleLensLeave);
      }
    }

    disconnectedCallback() {
      if (this.gallery) {
        this.gallery.removeEventListener('click', this.handleGalleryClick);
      }
      if (this.viewport) {
        this.viewport.removeEventListener('pointermove', this.handleLensMove);
        this.viewport.removeEventListener('pointerleave', this.handleLensLeave);
      }
    }

    show(opener) {
      if (!this.isDesktop()) return;
      super.show(opener);
      this.opener = opener;
      if (!this.buildSlides()) {
        this.hide();
        return;
      }
      this.goTo(this.getStartIndex());
      this.addEventListener('keydown', this.handleKeydown);
    }

    hide() {
      super.hide();
      this.removeEventListener('keydown', this.handleKeydown);
      this.hideLens();
    }

    handleGalleryClick(event) {
      if (!this.isDesktop()) return;
      const media = event.target.closest('.product__media');
      if (!media || !this.gallery?.contains(media)) return;
      const slide = media.closest('[data-zoom-src]');
      if (!slide) return;
      const opener = media.closest('modal-opener')?.querySelector('button');
      if (!opener) return;
      event.preventDefault();
      this.show(opener);
    }

    buildSlides() {
      const galleryId = this.dataset.galleryId;
      const gallery = galleryId ? document.getElementById(galleryId) : null;
      const items = gallery ? Array.from(gallery.querySelectorAll('[data-zoom-src]')) : [];

      if (!items.length || !this.track) {
        this.track && (this.track.innerHTML = '');
        this.updateCounter(0, 0);
        this.updateNav(0, 0);
        return false;
      }

      const signature = items.map((item) => item.dataset.zoomSrc || '').join('|');
      if (signature === this.cacheKey && this.slides.length) return true;

      this.cacheKey = signature;
      this.track.innerHTML = '';
      this.slides = items.map((item, index) => {
        const slide = document.createElement('div');
        slide.className = 'product-zoom-modal__slide';
        slide.dataset.index = String(index);
        slide.dataset.mediaId = item.dataset.zoomMediaId || '';
        slide.dataset.src = item.dataset.zoomSrc || '';
        slide.dataset.fallbackSrc = item.dataset.zoomSrcFallback || '';
        slide.dataset.srcset = item.dataset.zoomSrcset || '';
        slide.dataset.sizes = item.dataset.zoomSizes || '100vw';
        slide.dataset.alt = item.dataset.zoomAlt || '';
        slide.dataset.width = item.dataset.zoomWidth || '';
        slide.dataset.height = item.dataset.zoomHeight || '';

        const img = document.createElement('img');
        img.className = 'product-zoom-modal__image';
        img.alt = slide.dataset.alt;
        img.decoding = 'async';
        img.loading = 'lazy';

        const width = parseInt(slide.dataset.width, 10);
        const height = parseInt(slide.dataset.height, 10);
        if (!Number.isNaN(width) && !Number.isNaN(height)) {
          img.width = width;
          img.height = height;
        }

        slide.appendChild(img);
        this.track.appendChild(slide);
        return slide;
      });

      return true;
    }

    getStartIndex() {
      const openerMediaId = this.opener?.dataset?.mediaId;
      if (!openerMediaId) return 0;
      const matchIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === openerMediaId);
      return matchIndex >= 0 ? matchIndex : 0;
    }

    goTo(index) {
      if (!this.slides.length) return;
      const maxIndex = this.slides.length - 1;
      const nextIndex = Math.min(Math.max(index, 0), maxIndex);
      this.currentIndex = nextIndex;
      this.classList.remove('is-loading');

      if (this.track) {
        this.track.style.transform = `translateX(-${nextIndex * 100}%)`;
      }

      this.updateCounter(nextIndex + 1, this.slides.length);
      this.updateNav(nextIndex, maxIndex);
      this.hideLens();
      this.loadSlide(nextIndex, true);
      this.loadSlide(nextIndex - 1, false);
      this.loadSlide(nextIndex + 1, false);
    }

    loadSlide(index, isActive) {
      const slide = this.slides[index];
      if (!slide || slide.dataset.loaded === 'true') return;
      const img = slide.querySelector('img');
      if (!img) return;

      const slideIndex = index;
      slide.dataset.loaded = 'true';
      slide.classList.add('is-loading');
      if (isActive) this.classList.add('is-loading');

      img.fetchPriority = isActive ? 'high' : 'low';
      img.sizes = slide.dataset.sizes || '100vw';
      if (slide.dataset.srcset) img.srcset = slide.dataset.srcset;
      if (slide.dataset.src) img.src = slide.dataset.src;

      img.onload = () => {
        slide.classList.remove('is-loading');
        slide.classList.add('is-loaded');
        if (this.currentIndex === slideIndex) this.classList.remove('is-loading');
      };

      img.onerror = () => {
        if (slide.dataset.fallbackSrc && slide.dataset.fallbackApplied !== 'true') {
          slide.dataset.fallbackApplied = 'true';
          img.removeAttribute('srcset');
          img.src = slide.dataset.fallbackSrc;
          return;
        }
        slide.classList.remove('is-loading');
        if (this.currentIndex === slideIndex) this.classList.remove('is-loading');
      };
    }

    updateCounter(current, total) {
      if (!this.counter) return;
      if (!total) {
        this.counter.textContent = '';
        return;
      }
      this.counter.textContent = `${current} / ${total}`;
    }

    updateNav(current, maxIndex) {
      if (this.prevButton) this.prevButton.disabled = current <= 0;
      if (this.nextButton) this.nextButton.disabled = current >= maxIndex;
    }

    handleKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1);
      }
    }

    getActiveImage() {
      const slide = this.slides[this.currentIndex];
      const img = slide?.querySelector('img');
      if (!img || !img.complete || !img.currentSrc) return null;
      return img;
    }

    handleLensMove(event) {
      if (!this.isDesktop()) return;
      const img = this.getActiveImage();
      if (!img || !this.lens || !this.viewport) return;

      const imgRect = img.getBoundingClientRect();
      const viewportRect = this.viewport.getBoundingClientRect();
      const withinX = event.clientX >= imgRect.left && event.clientX <= imgRect.right;
      const withinY = event.clientY >= imgRect.top && event.clientY <= imgRect.bottom;
      if (!withinX || !withinY) {
        this.hideLens();
        return;
      }

      const radius = this.lensSize / 2;
      const relX = event.clientX - imgRect.left;
      const relY = event.clientY - imgRect.top;

      const lensX = event.clientX - viewportRect.left - radius;
      const lensY = event.clientY - viewportRect.top - radius;
      this.lens.style.left = `${lensX}px`;
      this.lens.style.top = `${lensY}px`;
      this.lens.classList.add('is-active');

      const zoomedWidth = imgRect.width * this.zoomScale;
      const zoomedHeight = imgRect.height * this.zoomScale;
      const bgPosX = -relX * this.zoomScale + radius;
      const bgPosY = -relY * this.zoomScale + radius;

      this.lens.style.backgroundImage = `url("${img.currentSrc}")`;
      this.lens.style.backgroundSize = `${zoomedWidth}px ${zoomedHeight}px`;
      this.lens.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
    }

    handleLensLeave() {
      this.hideLens();
    }

    hideLens() {
      if (!this.lens) return;
      this.lens.classList.remove('is-active');
    }
  }

  customElements.define('product-zoom-modal', ProductZoomModal);
}
